import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { CustomerPaymentMethod } from 'src/features/paymentMethods/utils/types';
import {
    customerPaymentMethodFormSchema,
    CustomerPaymentMethodFormSchemaValues,
} from 'src/features/paymentMethods/utils/formSchema';
import { useGetCustomerPaymentMethod } from 'src/features/paymentMethods/api/useGetCustomerPaymentMethod';
import CustomerPaymentMethodForm from 'src/features/paymentMethods/components/forms/CustomerPaymentMethodForm';
import { useParams } from 'react-router-dom';

interface CustomerPaymentMethodFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCustomerPaymentMethod?: CustomerPaymentMethod;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: CustomerPaymentMethodFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function CustomerPaymentMethodFormModal<TData>({
    selectedCustomerPaymentMethod,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: CustomerPaymentMethodFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { customerId = '' } = useParams();

    const { data: customerPaymentMethod } = useGetCustomerPaymentMethod({
        customerPaymentMethodId: selectedCustomerPaymentMethod?.id ?? '',
        config: { enabled: !!selectedCustomerPaymentMethod?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            name: customerPaymentMethod?.name ?? '',
            valid_from: customerPaymentMethod?.valid_from ?? undefined,
            valid_to: customerPaymentMethod?.valid_to ?? undefined,
            type: customerPaymentMethod?.type ?? undefined,
            is_primary: customerPaymentMethod?.is_primary ?? undefined,
            token: customerPaymentMethod?.token ?? undefined,
            customer: customerPaymentMethod?.customer.id ?? customerId ?? undefined,
        };
    }, [customerPaymentMethod, customerId]);

    const form = useForm<CustomerPaymentMethodFormSchemaValues>({
        resolver: zodResolver(customerPaymentMethodFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: CustomerPaymentMethodFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <CustomerPaymentMethodForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedCustomerPaymentMethod && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CustomerPaymentMethodFormModal;
